import React, { useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { submitLead, trackApplyClick } from "../lib/api";
import { CheckCircle2 } from "lucide-react";

const ApplyDialog = ({ product, trigger }) => {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [form, setForm] = useState({
        full_name: "", email: "", phone: "",
        employment_status: "", monthly_income: "", message: ""
    });

    const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await submitLead({
                product_id: product.id,
                full_name: form.full_name,
                email: form.email,
                phone: form.phone,
                employment_status: form.employment_status,
                monthly_income: Number(form.monthly_income) || 0,
                message: form.message,
            });
            setDone(true);
            toast.success("Application received");
        } catch (err) {
            toast.error("Could not submit. Check your details.");
        } finally {
            setSubmitting(false);
        }
    };

    const onOpenChange = (v) => {
        setOpen(v);
        if (v) {
            // Track click for affiliate billing the moment Apply is opened
            trackApplyClick(product.id);
        }
        if (!v) {
            setTimeout(() => {
                setDone(false);
                setForm({ full_name: "", email: "", phone: "", employment_status: "", monthly_income: "", message: "" });
            }, 200);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="max-w-lg bg-[#F8F7F4] border border-[#0B192C]/20 rounded-none" data-testid="apply-dialog">
                {done ? (
                    <div className="py-8 text-center">
                        <CheckCircle2 className="w-12 h-12 text-[#2F855A] mx-auto mb-4" strokeWidth={1.5} />
                        <h2 className="font-serif-display text-3xl mb-2">Application Received</h2>
                        <p className="text-sm text-[#4A5568] mb-6 max-w-sm mx-auto">
                            Your request for <strong>{product.name}</strong> has been logged. A representative from {product.provider} will reach out shortly.
                        </p>
                        <button
                            data-testid="apply-close-btn"
                            onClick={() => onOpenChange(false)}
                            className="btn-primary px-6 py-2.5 label-overline"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <>
                        <DialogHeader>
                            <div className="label-overline text-[#718096] mb-1">{product.provider}</div>
                            <DialogTitle className="font-serif-display text-3xl tracking-tight">Apply for {product.name}</DialogTitle>
                            <DialogDescription className="text-sm text-[#4A5568]">
                                We&rsquo;ll forward your details directly to the lender. No spam.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={onSubmit} className="space-y-4 mt-2">
                            <div>
                                <Label className="label-overline">Full Name</Label>
                                <Input
                                    required
                                    data-testid="apply-full-name"
                                    value={form.full_name}
                                    onChange={(e) => update("full_name", e.target.value)}
                                    className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body bg-white"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="label-overline">Email</Label>
                                    <Input
                                        type="email" required
                                        data-testid="apply-email"
                                        value={form.email}
                                        onChange={(e) => update("email", e.target.value)}
                                        className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body bg-white"
                                    />
                                </div>
                                <div>
                                    <Label className="label-overline">Phone</Label>
                                    <Input
                                        required
                                        data-testid="apply-phone"
                                        value={form.phone}
                                        onChange={(e) => update("phone", e.target.value)}
                                        placeholder="+1 868 ..."
                                        className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body bg-white"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="label-overline">Employment</Label>
                                    <Select value={form.employment_status} onValueChange={(v) => update("employment_status", v)}>
                                        <SelectTrigger data-testid="apply-employment" className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body bg-white">
                                            <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="employed">Employed</SelectItem>
                                            <SelectItem value="self-employed">Self-Employed</SelectItem>
                                            <SelectItem value="contract">Contract</SelectItem>
                                            <SelectItem value="retired">Retired</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="label-overline">Monthly Income (TT$)</Label>
                                    <Input
                                        type="number"
                                        data-testid="apply-income"
                                        value={form.monthly_income}
                                        onChange={(e) => update("monthly_income", e.target.value)}
                                        className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body bg-white"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="label-overline">Notes (optional)</Label>
                                <Textarea
                                    data-testid="apply-message"
                                    value={form.message}
                                    onChange={(e) => update("message", e.target.value)}
                                    rows={3}
                                    className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body bg-white"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="apply-submit-btn"
                                className="btn-accent w-full px-6 py-3 label-overline disabled:opacity-50"
                            >
                                {submitting ? "Submitting…" : "Submit Application"}
                            </button>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ApplyDialog;
